// Utilities to mirror legacy (Vue) loan calculations
// Keep logic aligned with assets/js/components/helpers.js (getLoanMonthLeft, getLoanSumLeft)

// Mois révolus, en rejouant l'emprunt de jours de PHP DateTime::diff : depuis un 31, aucun mois n'est révolu avant d'atteindre un 31
const completedMonthsSince = (start: Date, target: Date): number => {
    let months = (target.getFullYear() - start.getFullYear()) * 12 + (target.getMonth() - start.getMonth())
    let days = target.getDate() - start.getDate()

    for (let borrowed = 1; days < 0 && months > 0; borrowed += 1) {
        months -= 1
        days += new Date(target.getFullYear(), target.getMonth() - borrowed + 1, 0).getDate()
    }

    return days < 0 ? months - 1 : months
}

// DD/MM/YYYY d'abord, sinon parsing Date natif (ISO)
const parseSignatureDate = (value: string): Date | null => {
    const parts = value.split('/')
    if (parts.length === 3) {
        const [day, month, year] = parts.map((p) => parseInt(p, 10))
        const date = new Date(year, month - 1, day)
        if (!Number.isNaN(date.getTime())) return date
    }
    const date = new Date(value)
    return Number.isNaN(date.getTime()) ? null : date
}

/**
 * Mois révolus depuis la signature (0 pour une signature à venir) : le référentiel de toutes les données « restantes ».
 * @param signatureDate DD/MM/YYYY or ISO date string
 * @param monthToAdd offset months (defaults to 0, legacy used 1)
 */
export const computeMonthsSinceSignature = (
    signatureDate: string | null | undefined,
    monthToAdd = 0
): number | null => {
    if (!signatureDate) return null

    const start = parseSignatureDate(signatureDate)
    if (!start) return null

    const target = new Date()
    target.setHours(0, 0, 0, 0)
    if (monthToAdd) {
        target.setMonth(target.getMonth() + monthToAdd)
    }

    return Math.max(0, completedMonthsSince(start, target))
}

/**
 * Compute remaining months on a loan from its start date and total duration.
 * @param signatureDate DD/MM/YYYY or ISO date string
 * @param totalMonths total duration of the loan in months
 * @param monthToAdd offset months (defaults to 0, legacy used 1)
 */
export const computeLoanMonthsLeft = (
    signatureDate: string | null | undefined,
    totalMonths: number | null | undefined,
    monthToAdd = 0
): number | null => {
    if (!totalMonths) return null

    const monthsSince = computeMonthsSinceSignature(signatureDate, monthToAdd)
    if (monthsSince === null) return null

    return Math.max(0, totalMonths - monthsSince)
}

// Le différé stocké (time_offset) court depuis la signature, comme dans Symfony (differeRestant, getLoanSumLeft) ;
// en capital restant le formulaire saisit le différé restant à courir, compté depuis aujourd'hui (RM-920)
export const computeRemainingDeferral = (initialDeferral: number, signatureDate: string | null | undefined): number =>
    Math.max(0, initialDeferral - (computeMonthsSinceSignature(signatureDate) ?? 0))

export const computeInitialDeferral = (remainingDeferral: number, signatureDate: string | null | undefined): number =>
    remainingDeferral + (computeMonthsSinceSignature(signatureDate) ?? 0)

// time_offset attendu par le back (différé depuis la signature) à partir des champs différé du formulaire prêt
export const timeOffsetFromLoanForm = (
    form: { has_delay?: boolean; delay_duration?: number; save_amount_left?: boolean },
    signatureDate: string | null | undefined
): number => {
    if (!form.has_delay || !form.delay_duration) return 0

    return form.save_amount_left ? computeInitialDeferral(form.delay_duration, signatureDate) : form.delay_duration
}

// Mirrors Symfony InsuranceCalculator::getLastPalierSumFromRemaining so the displayed auto stage matches the stored quote
// Les paliers saisis décrivent l'échéancier à venir : rejeu sur le capital courant puis amortissement du solde
export const computeAutoStageAmount = (
    principal: number,
    annualRate: number,
    knownStages: { amount: number | undefined; month_duration: number | undefined }[],
    lastStageDuration: number
): number => {
    if (lastStageDuration <= 0) return 0

    const monthlyRate = annualRate / 1200
    let remaining = principal
    for (const stage of knownStages) {
        // 0 € (différé total) doit rejouer la période pour capitaliser les intérêts, comme le Symfony miroir
        if (stage.amount === undefined || !stage.month_duration) continue
        for (let i = 0; i < stage.month_duration; i++) {
            remaining -= stage.amount - remaining * monthlyRate
        }
    }

    remaining = Math.max(0, remaining)
    if (annualRate === 0) return remaining / lastStageDuration

    const compounded = Math.pow(1 + monthlyRate, lastStageDuration)
    return (remaining * compounded * monthlyRate) / (compounded - 1)
}

/**
 * Compute remaining capital on a loan (flat amortization approximation).
 * Mirrors legacy getLoanSumLeft logic.
 */
export const computeLoanSumLeft = (
    totalMonths: number,
    sum: number,
    rate: number,
    timeOffset: number,
    monthsLeft: number
): number => {
    const since = totalMonths - monthsLeft

    // Still in deferral: amount unchanged
    if (since <= timeOffset) {
        return sum
    }

    const refundMonths = totalMonths - timeOffset

    const getLoanMonthSumDenominator = (percent: number, months: number) => {
        return 1 - (1 + percent / 12) ** -months
    }

    const getLoanMonthSum = (capital: number, percent: number, months: number) => {
        return (capital * (percent / 12)) / getLoanMonthSumDenominator(percent, months)
    }

    const percent = rate / 100
    const denominator = getLoanMonthSumDenominator(percent, refundMonths)
    const perMonth = denominator > 0 ? getLoanMonthSum(sum, percent, refundMonths) : sum / refundMonths

    let remaining = sum
    for (let i = 1; i <= since - timeOffset; i++) {
        const interest = (percent / 12) * remaining
        remaining -= perMonth - interest
    }

    return Math.floor(remaining)
}
